import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { COUNTRIES } from "../lib/countries";
import { toast } from "sonner";

/**
 * Google sign-in callback page. Receives `?session_id=...` (App.js hoists
 * the URL fragment `#session_id=...` to a query string so React Router
 * sees it cleanly).
 *
 * Flow:
 *   1. POST /auth/google/exchange → { status, ... }
 *   2. status="linked" or "logged_in" → store JWT + redirect home
 *   3. status="needs_username"        → show username + nationality picker,
 *                                       POST /auth/google/complete on submit.
 */
export default function GoogleAuthCallback() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { hydrateFromToken } = useAuth();
  const { t } = useT();

  const [phase, setPhase] = useState("loading"); // loading | needs_username | error
  const [pickerData, setPickerData] = useState(null);
  const [username, setUsername] = useState("");
  const [nationality, setNationality] = useState("AE");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ranOnce = useRef(false);

  // Read "where did the sign-in originate?" hint stashed by AuthGate / V1 Login.
  // Defaults to "/" so existing V1 behaviour is preserved.
  const postLoginRedirect = (() => {
    try {
      const v = localStorage.getItem("eplay:postLoginRedirect");
      if (v && v.startsWith("/")) return v;
    } catch { /* noop */ }
    return "/";
  })();
  const clearRedirectHint = () => {
    try { localStorage.removeItem("eplay:postLoginRedirect"); } catch { /* noop */ }
  };

  useEffect(() => {
    if (ranOnce.current || !sessionId) return;
    ranOnce.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
        if (data.status === "linked" || data.status === "logged_in") {
          if (hydrateFromToken) {
            await hydrateFromToken(data.token, data.user);
          } else {
            localStorage.setItem("gml_token", data.token);
          }
          toast.success(data.status === "linked" ? t("google.linkedToast") : t("google.welcomeBack"));
          clearRedirectHint();
          navigate(postLoginRedirect, { replace: true });
        } else if (data.status === "needs_username") {
          setUsername(data.suggested_username || "");
          setPickerData({
            email: data.email,
            name: data.name,
            picture: data.picture,
            suggested_username: data.suggested_username,
          });
          setPhase("needs_username");
        }
      } catch (e) {
        setErr(formatApiError(e));
        setPhase("error");
      }
    })();
    // eslint-disable-next-line
  }, [sessionId]);

  const completeSignup = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/google/complete", {
        session_id: sessionId,
        username: username.trim(),
        nationality: nationality || null,
      });
      if (hydrateFromToken) {
        await hydrateFromToken(data.token, data.user);
      } else {
        localStorage.setItem("gml_token", data.token);
      }
      toast.success(t("google.welcomeToast"));
      clearRedirectHint();
      navigate(postLoginRedirect, { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (!sessionId) {
    return (
      <Panel testId="google-auth-no-session" className="font-mono text-sm text-[#71717A]">
        {t("google.noSession")}
      </Panel>
    );
  }

  if (phase === "loading") return <TactLoader label={t("google.signingIn")} />;

  if (phase === "error") return (
    <Panel testId="google-auth-error" className="border border-[#E11D48]">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48] mb-2">// {t("google.failed")}</div>
      <p className="text-sm font-body">{err || "Unknown error"}</p>
      <button
        type="button"
        onClick={() => navigate("/login")}
        className="mt-4 inline-block bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 hover:bg-[#6D28D9] transition-colors"
        data-testid="google-back-login"
      >
        {t("google.backLogin")}
      </button>
    </Panel>
  );

  // needs_username
  return (
    <div className="max-w-md mx-auto" data-testid="google-auth-needs-username">
      <SectionTitle kicker={`// ${t("google.welcome")}`}>{t("google.pickUsername")}</SectionTitle>
      <p className="font-body text-sm text-[#71717A] mb-6">
        {t("google.pickUsernameHelp", { email: pickerData?.email || "" })}
      </p>
      <form onSubmit={completeSignup} className="space-y-4">
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          minLength={2}
          maxLength={24}
          autoFocus
          data-testid="google-pick-username"
          className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
        />
        <select
          value={nationality}
          onChange={(e) => setNationality(e.target.value)}
          data-testid="google-pick-nationality"
          className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
        >
          <option value="">{t("google.pickNationalityOpt")}</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        {err && <div className="font-mono text-[11px] text-[#E11D48]" data-testid="google-pick-error">{err}</div>}
        <button
          type="submit"
          disabled={busy || username.trim().length < 2}
          data-testid="google-pick-submit"
          className="w-full bg-[#7C3AED] text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-3 disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
        >
          {busy ? t("google.creating") : t("google.create")}
        </button>
      </form>
    </div>
  );
}
